/**
 * EstimateSplitCard Component
 * Displays a single supplier's delivery options in the modal
 * Handles both regular suppliers and remaining items (undefined supplierId)
 */

import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Flex,
  Icon,
  Radio,
  RadioGroup,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Badge,
  Tag,
} from "@chakra-ui/react";
import { MdLocationOn } from "react-icons/md";
import { EstimateSplitCardProps, ManualDeliveryOptionEntry } from "./types";
import { getVehicleTypeDisplay } from "./utils/vehicleTypeUtils";
import { useManualOptionHandlers } from "./hooks/useManualOptionHandlers";
import { formatCPTRange } from "./utils/deliveryTimeUtils";
import { calculateDiscountedPrice } from "./utils/deliveryCostUtils";
import { calculateSupplierFulfillingItemsMRP } from "./utils/deliveryItemUtils";
import { formatDeliveryModeLabel } from "../../utils/DeliveryMode";
import { DeliveryModes } from "../../views/Orders/enums";

interface ActionPillButtonProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  isDisabled?: boolean;
  colorScheme?: "gray" | "orange";
}

const ActionPillButton: React.FC<ActionPillButtonProps> = ({
  label,
  isSelected,
  onClick,
  isDisabled = false,
  colorScheme = "gray",
}) => {
  const colors = {
    gray: {
      border: "yellow.300",
      text: "yellow.600",
      selectedBg: "yellow.100",
      selectedBorder: "yellow.500",
      selectedText: "yellow.700",
      hoverBorder: "yellow.300",
      hoverBg: "yellow.100",
    },
    orange: {
      border: "orange.300",
      text: "orange.500",
      selectedBg: "orange.100",
      selectedBorder: "orange.500",
      selectedText: "orange.700",
      hoverBorder: "orange.300",
      hoverBg: "orange.100",
    },
  };

  const scheme = colors[colorScheme];

  return (
    <Box
      as="button"
      w="70px"
      h="32px"
      px={1}
      py={1}
      borderRadius="full"
      border="1px solid"
      borderColor={isSelected ? scheme.selectedBorder : scheme.border}
      bg={isSelected ? scheme.selectedBg : "white"}
      cursor={isDisabled ? "not-allowed" : "pointer"}
      opacity={isDisabled ? 0.5 : 1}
      transition="all 0.3s ease-in-out"
      onClick={onClick}
      disabled={isDisabled}
      _hover={{
        borderColor: isDisabled ? "gray.400" : scheme.hoverBorder,
        bg: isDisabled ? "white" : scheme.hoverBg,
      }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text
        fontSize="0.8rem"
        fontWeight={isSelected ? "700" : "600"}
        color={isSelected ? scheme.selectedText : scheme.text}
        fontFamily="'Nunito','Inter', sans-serif"
      >
        {label}
      </Text>
    </Box>
  );
};

const EstimateSplitCard: React.FC<EstimateSplitCardProps> = ({
  supplier,
  selection,
  onSelectionChange,
  isExpired,
  quickEtasMins,
}) => {
  const [buyTimeValue, setBuyTimeValue] = useState<string>(() => {
    if (supplier.buyTime) {
      const minutes = parseInt(String(supplier.buyTime));
      if (!isNaN(minutes) && minutes > 0) {
        const hours = minutes / 60;
        // Round to 1 decimal place
        const roundedHours = Math.round(hours * 10) / 10;
        return String(roundedHours);
      }
    }
    return "";
  });

  const isRemainingItems =
    supplier.supplierId === null || supplier.supplierId === undefined;

  const supplierFulfillingItemsMRP = useMemo(() => {
    return calculateSupplierFulfillingItemsMRP(supplier);
  }, [supplier]);

  const handleManualOptionUpdate = useCallback(
    (entry: ManualDeliveryOptionEntry) => {
      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "manual",
          manualEntry: entry,
        },
      });
    },
    [supplier.supplierId, onSelectionChange]
  );

  const {
    quickEtaOptions,
    handleManualCPTChange,
    handleManualChargeChange,
    handleQuickEtaSelect,
  } = useManualOptionHandlers({
    isExpired,
    quickEtasMins,
    currentManualEntry: selection?.deliveryDetails?.manualEntry,
    onManualEntryChange: handleManualOptionUpdate,
  });

  // Handle option select by index
  const handleOptionSelect = useCallback(
    (index: number) => {
      if (!supplier.deliveryOptions || index >= supplier.deliveryOptions.length)
        return;

      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "option",
          selectedOptionIndex: index,
        },
      });
    },
    [supplier.supplierId, supplier.deliveryOptions, onSelectionChange]
  );

  const selectedOptionIndex =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "option"
      ? selection.deliveryDetails.selectedOptionIndex
      : undefined;

  const isManualSelected =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "manual";

  const handleManualOptionSelect = useCallback(() => {
    if (isManualSelected) return;

    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction: "confirm",
      deliveryDetails: {
        selectionType: "manual",
        manualEntry: selection?.deliveryDetails?.manualEntry || {
          cpt: "",
          deliveryCharge: 0,
        },
      },
    });
  }, [isManualSelected, supplier.supplierId, selection, onSelectionChange]);

  const handleHold = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction: selection?.customerAction === "hold" ? "confirm" : "hold",
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const handleCancel = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction:
        selection?.customerAction === "cancel" ? "confirm" : "cancel",
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const displayTitle = isRemainingItems
    ? "Supplier Not Found"
    : supplier.supplierName;

  const radioValue = useMemo(() => {
    if (isManualSelected) return "manual";
    if (selectedOptionIndex !== undefined) return String(selectedOptionIndex);
    return "";
  }, [isManualSelected, selectedOptionIndex]);

  const isBuyTimeSelected = selection?.customerAction === "buytime";

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="orange.300"
      overflow="hidden"
      mb={4}
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ boxShadow: "md" }}
    >
      {/* Supplier Header */}
      <Box
        bg="gray.50"
        px={4}
        py={3}
        borderBottom="1px solid"
        borderColor="orange.200"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={3} align="flex-start">
            <VStack align="flex-start" spacing={0}>
              <Text
                fontSize="0.95rem"
                fontWeight="800"
                color="gray.700"
                fontFamily="'Nunito','Inter', sans-serif"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {displayTitle}
              </Text>
              {!isRemainingItems && supplier.supplierCity && (
                <HStack spacing={1}>
                  <MdLocationOn size="12" color="#3b82f6" />
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="600"
                  >
                    {supplier.supplierCity}
                  </Text>
                </HStack>
              )}
            </VStack>
            <HStack spacing={2} align="center">
              {!isRemainingItems && supplier.distanceKm !== undefined && (
                <HStack
                  spacing={1}
                  bg="white"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <MdLocationOn size="12" color="#3b82f6" />
                  <Text
                    fontSize="0.7rem"
                    color="gray.500"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="600"
                  >
                    {supplier.distanceKm.toFixed(1)} km
                  </Text>
                </HStack>
              )}
              {!isRemainingItems && supplier.vehicleType !== undefined && (
                <HStack
                  spacing={1}
                  bg="white"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text
                    fontSize="0.7rem"
                    color="gray.500"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                  >
                    {getVehicleTypeDisplay(supplier.vehicleType)?.label || ""}
                  </Text>
                  {getVehicleTypeDisplay(supplier.vehicleType)?.icon && (
                    <Icon
                      as={getVehicleTypeDisplay(supplier.vehicleType)?.icon}
                      color="blue.500"
                      boxSize={3}
                    />
                  )}
                </HStack>
              )}
              <HStack
                spacing={1}
                bg="white"
                px={2}
                py={0.5}
                borderRadius="full"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="0.7rem"
                  color="gray.500"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                >
                  MRP: ₹{supplierFulfillingItemsMRP.toLocaleString("en-IN")}
                </Text>
              </HStack>
            </HStack>
          </HStack>

          {/* Action Area */}
          <VStack align="flex-end" spacing={1}>
            {/* Action Buttons: Hold | Cancel split | Buy time - Pill style */}
            <HStack spacing={3} align="center">
              <ActionPillButton
                label="HOLD"
                isSelected={selection?.customerAction === "hold"}
                onClick={handleHold}
                isDisabled={isExpired}
                colorScheme="gray"
              />
              <ActionPillButton
                label="CANCEL"
                isSelected={selection?.customerAction === "cancel"}
                onClick={handleCancel}
                isDisabled={isExpired}
                colorScheme="orange"
              />
              <VStack spacing={0.5} align="center">
                <Box
                  as="button"
                  px={3}
                  py={1}
                  h="32px"
                  borderRadius="full"
                  border="1px solid"
                  borderColor={isBuyTimeSelected ? "blue.500" : "gray.400"}
                  bg={isBuyTimeSelected ? "blue.100" : "white"}
                  cursor={isExpired ? "not-allowed" : "pointer"}
                  opacity={isExpired ? 0.5 : 1}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  transition="all 0.2s ease"
                  _hover={
                    isExpired
                      ? {}
                      : {
                          borderColor: isBuyTimeSelected
                            ? "blue.600"
                            : "blue.300",
                          bg: isBuyTimeSelected ? "blue.100" : "blue.50",
                        }
                  }
                  _active={
                    isExpired
                      ? {}
                      : {
                          borderColor: "blue.600",
                          bg: "blue.100",
                        }
                  }
                  onClick={() => {
                    if (isExpired) return;
                    const newAction = isBuyTimeSelected ? "confirm" : "buytime";

                    onSelectionChange({
                      supplierId: supplier.supplierId,
                      customerAction: newAction,
                      buyTimeHours:
                        newAction === "buytime" ? buyTimeValue : undefined,
                    });
                  }}
                >
                  <Text
                    fontSize="0.8rem"
                    fontWeight={isBuyTimeSelected ? "700" : "600"}
                    color={isBuyTimeSelected ? "blue.700" : "gray.700"}
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    BUY TIME
                  </Text>
                  <InputGroup
                    size="xs"
                    w="55px"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Input
                      type="number"
                      min={0.1}
                      step={0.1}
                      placeholder="-"
                      textAlign="center"
                      value={buyTimeValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBuyTimeValue(val);

                        if (!val) {
                          onSelectionChange({
                            supplierId: supplier.supplierId,
                            customerAction: "buytime",
                            buyTimeHours: undefined,
                          });
                          return;
                        }

                        const hours = parseFloat(val);
                        if (isNaN(hours) || hours <= 0) {
                          return;
                        }

                        onSelectionChange({
                          supplierId: supplier.supplierId,
                          customerAction: "buytime",
                          buyTimeHours: val,
                        });
                      }}
                      isDisabled={isExpired}
                      borderColor="gray.400"
                      borderRadius="full"
                      fontSize="0.75rem"
                      fontWeight="600"
                      bg="white"
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                      }}
                    />
                    <InputRightElement width="32px" pointerEvents="none">
                      <Text
                        fontSize="0.65rem"
                        color="gray.500"
                        mr={1}
                        fontWeight="800"
                      >
                        hrs
                      </Text>
                    </InputRightElement>
                  </InputGroup>
                </Box>
                <Box
                  h="0"
                  overflow="visible"
                  display="flex"
                  justifyContent="center"
                  w="full"
                >
                  <Text
                    fontSize="0.6rem"
                    fontWeight="600"
                    color={isBuyTimeSelected ? "blue.500" : "gray.600"}
                    fontFamily="'Nunito','Inter', sans-serif"
                    whiteSpace="nowrap"
                    mt="-2px"
                  >
                    Offline Discovery
                  </Text>
                </Box>
              </VStack>
            </HStack>
          </VStack>
        </Flex>
      </Box>

      {/* Parts and Delivery Selection Grid */}
      <Flex direction={{ base: "column", md: "row" }}>
        {/* Parts List - Scrollable */}
        <Box
          flex="1"
          px={4}
          py={3}
          borderRight={{ base: "none", md: "1px solid" }}
          borderBottom={{ base: "1px solid", md: "none" }}
          borderColor="gray.100"
          bg="gray.50"
        >
          <HStack mb={2} spacing={4}>
            <Text
              fontSize="0.7rem"
              color="gray.500"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="700"
              textTransform="uppercase"
              flex="1"
            >
              Part Details
            </Text>
            <Text
              fontSize="0.7rem"
              color="gray.500"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="700"
              textTransform="uppercase"
              w="40px"
              textAlign="right"
            >
              Qty
            </Text>
          </HStack>

          <Box
            maxH="180px"
            overflowY="auto"
            pr={1}
            css={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "#CBD5E0",
                borderRadius: "2px",
              },
            }}
          >
            <VStack align="stretch" spacing={2}>
              {supplier.parts.map((part, index) => (
                <HStack
                  key={`${part.partNumber}-${index}`}
                  spacing={4}
                  pb={1}
                  borderBottom="1px dashed"
                  borderColor="gray.200"
                  _last={{ borderBottom: "none" }}
                >
                  <VStack align="start" spacing={0} flex="1">
                    <Text
                      fontSize="0.8rem"
                      color="gray.700"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="700"
                      lineHeight="1"
                    >
                      {part.partNumber}
                    </Text>
                    <Text
                      fontSize="0.8rem"
                      color="gray.600"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="500"
                      mt="1"
                      lineHeight="1.1"
                      wordBreak="break-all"
                    >
                      {part.partName}
                    </Text>
                  </VStack>
                  <Text
                    fontSize="0.8rem"
                    color="gray.700"
                    fontFamily="'Nunito','Inter', sans-serif"
                    textAlign="right"
                    fontWeight="600"
                  >
                    {part.quantity}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* Delivery Selection */}
        <Box flex="1.4" px={5} py={4}>
          <Flex justify="space-between" align="center" mb={4} pl={0}>
            <HStack spacing={1}>
              <Text
                fontSize="0.85rem"
                fontWeight="700"
                color="gray.700"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                Delivery Options
              </Text>
              <Text color="red.500" fontWeight="bold">
                *
              </Text>
            </HStack>
            <Badge
              colorScheme="orange"
              variant="subtle"
              fontSize="0.65rem"
              mr={0}
            >
              SELECT ONE
            </Badge>
          </Flex>

          {/* Options List with Manual Entry */}
          <RadioGroup
            value={radioValue}
            onChange={(value) => {
              if (value === "manual") {
                handleManualOptionSelect();
              } else {
                handleOptionSelect(parseInt(value));
              }
            }}
            isDisabled={isExpired}
          >
            <VStack align="stretch" spacing={2}>
              {/* Table Header */}
              <HStack spacing={4} px={0} mb={1}>
                {/* Vehicle Type column removed as it's now at supplier level */}
                <Text
                  fontSize="0.7rem"
                  color="gray.500"
                  fontWeight="700"
                  textTransform="uppercase"
                  flex="1"
                >
                  CUSTOMER PROMISE TIME
                </Text>
                <Text
                  fontSize="0.7rem"
                  color="gray.500"
                  fontWeight="700"
                  textTransform="uppercase"
                  w="140px"
                  textAlign="right"
                >
                  Delivery Charge
                </Text>
              </HStack>

              {/* API Delivery Options */}
              {supplier.deliveryOptions.map((option, index) => {
                const isSelected = selectedOptionIndex === index;
                const showDeliveryModeBadge =
                  option.deliveryMode &&
                  option.deliveryMode !== DeliveryModes.DOOR_DELIVERY;

                return (
                  <HStack
                    key={`option-${index}`}
                    spacing={4}
                    p={3}
                    borderRadius="md"
                    bg={isSelected ? "blue.50" : "white"}
                    border="1px solid"
                    borderColor={isSelected ? "blue.400" : "gray.200"}
                    cursor={isExpired ? "not-allowed" : "pointer"}
                    opacity={isExpired ? 0.6 : 1}
                    transition="all 0.2s"
                    _hover={{
                      borderColor: isExpired ? "gray.200" : "blue.300",
                      bg: isExpired
                        ? undefined
                        : isSelected
                        ? "blue.50"
                        : "gray.50",
                      transform: isExpired ? "none" : "translateY(-1px)",
                      shadow: isExpired ? "none" : "sm",
                    }}
                    onClick={() => !isExpired && handleOptionSelect(index)}
                    position="relative"
                  >
                    <HStack flex="1" spacing={2}>
                      <Text
                        fontSize="0.85rem"
                        color="gray.700"
                        fontFamily="'Nunito','Inter', sans-serif"
                        fontWeight="600"
                      >
                        {formatCPTRange(option.cptMin, option.cptMax)}
                      </Text>
                      {showDeliveryModeBadge && (
                        <Badge
                          bg="#D8E6FD"
                          color="#1D3AA1"
                          fontSize="0.65rem"
                          fontWeight="700"
                          px={2}
                          py={0.5}
                          borderRadius="md"
                          textTransform="uppercase"
                        >
                          {formatDeliveryModeLabel(option.deliveryMode!)}
                        </Badge>
                      )}
                    </HStack>
                    {/* Delivery Charge - horizontal layout */}
                    {option.deliveryCostDiscountPercentage ? (
                      <HStack spacing={2} align="center">
                        <Box position="relative">
                          <Text
                            fontSize="0.8rem"
                            color="gray.500"
                            fontFamily="'Nunito','Inter', sans-serif"
                            fontWeight="600"
                          >
                            ₹{option.deliveryCost}
                          </Text>
                          {/* Diagonal Line Overlay */}
                          <Box
                            position="absolute"
                            top="50%"
                            left="-10%"
                            right="-10%"
                            height="1.2px"
                            bg="gray.500"
                            transform="rotate(-12deg)"
                            transformOrigin="center"
                            pointerEvents="none"
                          />
                        </Box>
                        <Text
                          fontSize="0.85rem"
                          color="gray.800"
                          fontFamily="'Nunito','Inter', sans-serif"
                          fontWeight="800"
                        >
                          ₹
                          {calculateDiscountedPrice(
                            option.deliveryCost,
                            option.deliveryCostDiscountPercentage
                          )}
                        </Text>
                      </HStack>
                    ) : (
                      <Text
                        fontSize="0.85rem"
                        color="gray.800"
                        fontFamily="'Nunito','Inter', sans-serif"
                        fontWeight="700"
                      >
                        ₹ {option.deliveryCost}
                      </Text>
                    )}
                  </HStack>
                );
              })}

              {/* Manual Entry Option - Always available */}
              <VStack
                align="stretch"
                spacing={2}
                p={3}
                borderRadius="md"
                bg={isManualSelected ? "blue.50" : "white"}
                border="1px solid"
                borderColor={isManualSelected ? "blue.400" : "gray.200"}
                cursor={isExpired ? "not-allowed" : "pointer"}
                opacity={isExpired ? 0.6 : 1}
                transition="all 0.2s"
                _hover={{
                  borderColor: isExpired ? "gray.200" : "blue.300",
                  bg: isExpired
                    ? undefined
                    : isManualSelected
                    ? "blue.50"
                    : "gray.50",
                  transform: isExpired ? "none" : "translateY(-1px)",
                  shadow: isExpired ? "none" : "sm",
                }}
                onClick={() => !isExpired && handleManualOptionSelect()}
              >
                <HStack spacing={3} align="center">
                  <Text
                    fontSize="0.75rem"
                    fontWeight="700"
                    textTransform="uppercase"
                    color="purple.600"
                    fontFamily="'Nunito','Inter', sans-serif"
                    letterSpacing="wider"
                  >
                    MANUAL ENTRY
                  </Text>
                </HStack>

                {/* Manual entry input fields - shown when selected */}
                {isManualSelected && (
                  <VStack
                    align="stretch"
                    spacing={3}
                    mt={2}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Quick ETA Tags */}
                    <Box>
                      <Text
                        fontSize="0.7rem"
                        color="gray.500"
                        fontWeight="700"
                        mb={2}
                      >
                        QUICK SELECT CPT
                      </Text>
                      <Flex wrap="wrap" gap={2}>
                        {quickEtaOptions.map((opt) => (
                          <Tag
                            key={opt.label}
                            size="sm"
                            variant="subtle"
                            colorScheme="blue"
                            cursor={isExpired ? "not-allowed" : "pointer"}
                            onClick={() => handleQuickEtaSelect(opt.minutes)}
                            opacity={isExpired ? 0.6 : 1}
                            _hover={{
                              bg: isExpired ? undefined : "blue.100",
                            }}
                            fontFamily="'Nunito','Inter', sans-serif"
                          >
                            {opt.label}
                          </Tag>
                        ))}
                      </Flex>
                    </Box>

                    {/* CPT and Delivery Charge Inputs */}
                    <HStack spacing={4} align="start">
                      <Input
                        type="datetime-local"
                        size="sm"
                        fontSize="0.85rem"
                        fontWeight="500"
                        placeholder="Select CPT"
                        borderColor={
                          selection?.deliveryDetails?.manualEntry?.cpt
                            ? "gray.300"
                            : "red.300"
                        }
                        _focus={{
                          borderColor: "blue.400",
                          boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                        }}
                        value={
                          selection?.deliveryDetails?.manualEntry?.cpt || ""
                        }
                        onChange={(e) => handleManualCPTChange(e.target.value)}
                        isDisabled={isExpired}
                        flex="1"
                      />
                      <InputGroup w="140px" size="sm">
                        <InputLeftElement
                          pointerEvents="none"
                          fontSize="0.8rem"
                          color="gray.500"
                        >
                          ₹
                        </InputLeftElement>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="0"
                          fontSize="0.85rem"
                          fontWeight="600"
                          textAlign="right"
                          value={
                            selection?.deliveryDetails?.manualEntry
                              ?.deliveryCharge || ""
                          }
                          onChange={handleManualChargeChange}
                          isDisabled={isExpired}
                          borderColor={
                            selection?.deliveryDetails?.manualEntry
                              ?.deliveryCharge
                              ? "gray.300"
                              : "red.300"
                          }
                          _focus={{
                            borderColor: "blue.400",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-blue-400)",
                          }}
                        />
                      </InputGroup>
                    </HStack>
                  </VStack>
                )}
              </VStack>
            </VStack>
          </RadioGroup>
        </Box>
      </Flex>
    </Box>
  );
};

export default EstimateSplitCard;
